import { useContext, useEffect, useMemo, useState } from 'react';

import { Notifications } from '@hrworks/sui-core/ClassicLayout';
import { PageContext } from '@hrworks/sui-core/Page';

import { Backdrop, Content, Header, Sidebars, type SqwLayoutProps } from './';
import { S } from './SqwLayout.styles';
import { type BreadcrumbOverride, SqwLayoutContext } from './SqwLayoutContext';

export const SqwLayout = ({ header, sidebar, content, desktopSidebarTogglerMode = 'none' }: SqwLayoutProps) => {
  const { notifications } = useContext(PageContext);
  // Desktop sidebar starts expanded (visible); users can collapse it via the toggle button
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(desktopSidebarTogglerMode !== 'none');
  const [isMobileSidebarExpanded, setIsMobileSidebarExpanded] = useState(false);

  // Safety: If backend switches mode to 'none' at runtime, ensure sidebar is expanded (no toggle button to recover)
  useEffect(() => {
    if (desktopSidebarTogglerMode === 'none') setIsSidebarExpanded(true);
  }, [desktopSidebarTogglerMode]);

  const [breadcrumbOverride, setBreadcrumbOverride] = useState<BreadcrumbOverride | null>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down' | null>(null);

  const isSidebarCollapsed = desktopSidebarTogglerMode === 'fully-collapse' && !isSidebarExpanded;

  const contextValue = useMemo(
    () => ({
      isSidebarExpanded,
      setIsSidebarExpanded,
      isMobileSidebarExpanded,
      setIsMobileSidebarExpanded,
      setBreadcrumbOverride,
      canScrollUp,
      canScrollDown,
      scrollDirection,
      desktopSidebarTogglerMode,
      isSidebarCollapsed,
    }),
    [
      isSidebarExpanded,
      isMobileSidebarExpanded,
      canScrollUp,
      canScrollDown,
      scrollDirection,
      desktopSidebarTogglerMode,
      isSidebarCollapsed,
    ],
  );

  const { breadcrumb, ...contentProps } = content || {};

  return (
    <SqwLayoutContext.Provider value={contextValue}>
      <S.LayoutContainer isSidebarCollapsed={isSidebarCollapsed}>
        <Header {...header} />
        <Sidebars header={header} sidebar={sidebar} />
        <Content
          {...contentProps}
          breadcrumb={breadcrumbOverride || breadcrumb}
          onCanScrollDownChange={setCanScrollDown}
          onCanScrollUpChange={setCanScrollUp}
          onScrollDirectionChange={setScrollDirection}
        />
        <Backdrop />
        <Notifications notifications={notifications} />
      </S.LayoutContainer>
    </SqwLayoutContext.Provider>
  );
};
